import type { TFunction } from "i18next";
import type { NavigationKey, IdentityInfo, ResolverOptions } from "./types";
import navigationContent from "../../../content/navigation";
import { getIncentiveViewID } from "../IncentivesViewID/IncentiveViewID";
import { AWARD_CODES } from "../../enums/award-codes";
import { ANZ_MARKET_CODES, ESAN_MARKET_CODES } from "../../constants/market-constants";
import { Config } from "../../interfaces/config";

/**
 * Canonical Navigation Keys - one constant per unique navigation key.
 * These are the actual values that resolve to labels in navigation.linkTitle.
 */
const NAV_KEYS = {
  // Core Incentives
  CSI: "csi",
  PERFORMANCE_PLUS: "performancePlus",
  PERFORMANCE_ELITE: "performanceElite",
  PERFORMANCE_PLUS_ELITE: "performancePlusElite",
  STRONG_START_INCENTIVE: "strongStartIncentive",
  PATH_TO_BRONZE: "pathToBronze",
  BRONZE_FOUNDATION: "bronzeFoundation",
  BRONZE_BUILDER: "bronzeBuilder",

  // PGG Family
  PERSONAL_GROWTH_MONTHLY: "personalGrowthMonthly",
  PERSONAL_GROWTH_ANNUAL: "personalGrowthAnnual",
  PERSONAL_GROUP_GROWTH: "personalGroupGrowth",
  PERSONAL_GROUP_GROWTH_GCR: "personalGroupGrowthGCR",
  PERSONAL_GROUP_TIERED_INCENTIVE: "personalGroupTieredIncentive",

  // FGG Family (dynamic resolution - see getFrontlineNavKey)
  FRONTLINE_GROWTH: "frontlineGrowth",
  LEADERSHIP_PLUS: "leadershipPlus",
  FRONTLINE_Q: "frontlineQ",

  // Q Family
  QCG: "qcg",
  VISION_BONUS: "visionBonus",
  MONTHLY_Q: "monthlyQ",
  Q_GROWTH_INCENTIVE: "qGrowthIncentive",
  ANNUAL_Q: "annualq",

  // Other Incentives
  TWO_TIME_CASH_INCENTIVE: "twoTimeCashIncentive",
  ONE_TIME_CASH_INCENTIVE: "otci",
  HEALTH_WELLBEING: "healthWellbeing",
  FOUNDERS_SALES_INCENTIVE: "foundersSalesIncentive",
  FOUNDERS_SALES_INCENTIVE_REPORT: "foundersSalesIncentiveReport",
  HAT: "hat",
  FAA: "faa",

  // Core Plan Bonuses
  TTCA: "ttca",
  EMERALD_BONUS: "emeraldBonus",
  DIAMOND_PLUS_BONUS: "diamondPlusBonus",
  FOUNDERS_PLATINUM: "foundersPlatinum",

  // Leadership Seminar (dynamic resolution - see getLeadershipSeminarNavKey)
  LEADERSHIP_SEMINAR: "leadershipSeminar",
  LEADERSHIP_SEMINAR_ESAN: "leadershipSeminarEsan",

  // A70 Family
  DIAMOND_BONUS: "diamondBonus",
  PATH_TO_A70: "pathToA70",
  A70_ELITE_BONUS: "a70EliteBonus",

  // Action Reports
  WHOS_NEW: "whosNew",
  WHOS_NEW_WITH_CUSTOMERS: "whosNewWithCustomers",
  WHOS_CLOSE: "whosClose",
  BIRTHDAYS: "birthdays",
  RENEWALS: "renewals",
  NON_BONUSABLE_BV: "nonBonusableBv",
  CVR_COMPLIANCE: "cvrCompliance",
  IBO_ONBOARDING: "iboOnboarding",
  CBR_CLOCK_CHECK: "cbrClockCheck",
  SAQ_CERTIFICATION: "saqCertification",
  SAQ_ACTION_REPORT: "saqActionReport",
  BRONZE_GROUP_TRACKING: "bronzeGroupTracking",
  SSI_UPLINE_REPORT: "ssiUplineReport",
  GROUP_SOP_TRACKING: "groupSopTracking",
  // Group Management
  LOS: "los",
  EDLOS: "edlos",
  WATCHLIST: "watchlist",
  CUSTOMER_MEMBER_LIST: "customerMemberList",
  GROUP_MANAGEMENT: "groupManagement",
  GROUP_MANAGEMENT_DASHBOARD: "groupManagementDashboard",
  COMEBACK_CASH_INCENTIVE: "cci",
  A70_TWO_TIME_CASH_AWARD: "a70Ttca",
} as const satisfies Record<string, NavigationKey>;

/**
 * Static mapping from backend identities to Navigation keys.
 * Multiple backend IDs can map to the same canonical navigation key.
 * This is the normalization layer that ensures consistent titles across all surfaces.
 */
const IDENTITY_TO_NAV_KEY: Record<string, NavigationKey> = {
  // Core Incentives
  csi: NAV_KEYS.CSI,
  performancePlus: NAV_KEYS.PERFORMANCE_PLUS,
  performanceElite: NAV_KEYS.PERFORMANCE_ELITE,
  performancePlusElite: NAV_KEYS.PERFORMANCE_PLUS_ELITE,
  ppe: NAV_KEYS.PERFORMANCE_PLUS_ELITE,
  strongStartIncentive: NAV_KEYS.STRONG_START_INCENTIVE,
  ssi: NAV_KEYS.STRONG_START_INCENTIVE,
  pathToBronze: NAV_KEYS.PATH_TO_BRONZE,
  newAboActivation: NAV_KEYS.PATH_TO_BRONZE,
  ptb: NAV_KEYS.PATH_TO_BRONZE,
  bronzeFoundation: NAV_KEYS.BRONZE_FOUNDATION,
  bfi: NAV_KEYS.BRONZE_FOUNDATION,
  bronzeBuilder: NAV_KEYS.BRONZE_BUILDER,
  bbi: NAV_KEYS.BRONZE_BUILDER,

  // PGG Family
  monthlyPgg: NAV_KEYS.PERSONAL_GROWTH_MONTHLY,
  monthlyPGG: NAV_KEYS.PERSONAL_GROWTH_MONTHLY,
  personalGrowthMonthly: NAV_KEYS.PERSONAL_GROWTH_MONTHLY,
  pgg: NAV_KEYS.PERSONAL_GROUP_GROWTH,
  pggAnnual: NAV_KEYS.PERSONAL_GROWTH_ANNUAL,
  annualPGG: NAV_KEYS.PERSONAL_GROWTH_ANNUAL,
  pga: NAV_KEYS.PERSONAL_GROWTH_ANNUAL,
  personalGrowthAnnual: NAV_KEYS.PERSONAL_GROWTH_ANNUAL,
  personalGroupGrowth: NAV_KEYS.PERSONAL_GROUP_GROWTH,
  pggGcr: NAV_KEYS.PERSONAL_GROUP_GROWTH_GCR,
  personalGroupGrowthGCR: NAV_KEYS.PERSONAL_GROUP_GROWTH_GCR,
  pgti: NAV_KEYS.PERSONAL_GROUP_TIERED_INCENTIVE,
  personalGroupTieredIncentive: NAV_KEYS.PERSONAL_GROUP_TIERED_INCENTIVE,

  // FGG Family
  fgi: NAV_KEYS.FRONTLINE_GROWTH,
  frontlineGrowth: NAV_KEYS.FRONTLINE_GROWTH,
  leadershipPlus: NAV_KEYS.LEADERSHIP_PLUS,
  frontlineQ: NAV_KEYS.FRONTLINE_Q,

  // Q Family
  qcg: NAV_KEYS.QCG,
  qcGrowth: NAV_KEYS.QCG,
  visionBonus: NAV_KEYS.VISION_BONUS,
  monthlyQ: NAV_KEYS.MONTHLY_Q,
  qGrowthIncentive: NAV_KEYS.Q_GROWTH_INCENTIVE,
  annualq: NAV_KEYS.ANNUAL_Q,

  // Other Incentives
  twoTimeCashIncentive: NAV_KEYS.TWO_TIME_CASH_INCENTIVE,
  ttci: NAV_KEYS.TWO_TIME_CASH_INCENTIVE,
  otci: NAV_KEYS.ONE_TIME_CASH_INCENTIVE,
  cci: NAV_KEYS.COMEBACK_CASH_INCENTIVE,
  a70Ttca: NAV_KEYS.A70_TWO_TIME_CASH_AWARD,
  healthWellbeing: NAV_KEYS.HEALTH_WELLBEING,
  healthAndWellbeing: NAV_KEYS.HEALTH_WELLBEING,
  foundersSalesIncentive: NAV_KEYS.FOUNDERS_SALES_INCENTIVE,
  fsi: NAV_KEYS.FOUNDERS_SALES_INCENTIVE,
  fsiActionReport: NAV_KEYS.FOUNDERS_SALES_INCENTIVE_REPORT,
  hat: NAV_KEYS.HAT,
  hatgar: NAV_KEYS.HAT,
  faa: NAV_KEYS.FAA,

  // Core Plan Bonuses
  ttca: NAV_KEYS.TTCA,
  emeraldBonus: NAV_KEYS.EMERALD_BONUS,
  diamondPlusBonus: NAV_KEYS.DIAMOND_PLUS_BONUS,
  foundersPlatinum: NAV_KEYS.FOUNDERS_PLATINUM,

  // Leadership Seminar
  leadershipSeminar: NAV_KEYS.LEADERSHIP_SEMINAR,

  // A70 Family
  diamondBonus: NAV_KEYS.DIAMOND_BONUS,
  pathToA70: NAV_KEYS.PATH_TO_A70,
  a70EliteBonus: NAV_KEYS.A70_ELITE_BONUS,

  // Action Reports - Backend configuration uses "Ar" suffix variants
  whosNew: NAV_KEYS.WHOS_NEW,
  whosNewWithCustomers: NAV_KEYS.WHOS_NEW_WITH_CUSTOMERS,
  whosClose: NAV_KEYS.WHOS_CLOSE,
  birthdays: NAV_KEYS.BIRTHDAYS,
  birthdaysAr: NAV_KEYS.BIRTHDAYS,
  renewals: NAV_KEYS.RENEWALS,
  renewalsAr: NAV_KEYS.RENEWALS,
  provisionalAr: NAV_KEYS.IBO_ONBOARDING,
  nonBonusableBv: NAV_KEYS.NON_BONUSABLE_BV,
  nonBonusableBvAr: NAV_KEYS.NON_BONUSABLE_BV,
  cvrCompliance: NAV_KEYS.CVR_COMPLIANCE,
  cvrAr: NAV_KEYS.CVR_COMPLIANCE,
  iboOnboarding: NAV_KEYS.IBO_ONBOARDING,
  iboOnboardingAr: NAV_KEYS.IBO_ONBOARDING,
  cbrClockCheck: NAV_KEYS.CBR_CLOCK_CHECK,
  cbrAr: NAV_KEYS.CBR_CLOCK_CHECK,
  saqCertification: NAV_KEYS.SAQ_CERTIFICATION,
  saqActionReport: NAV_KEYS.SAQ_ACTION_REPORT,
  bronzeGroupTracking: NAV_KEYS.BRONZE_GROUP_TRACKING,
  ssiUplineReport: NAV_KEYS.SSI_UPLINE_REPORT,
  groupSopTracking: NAV_KEYS.GROUP_SOP_TRACKING,

  // Group Management
  los: NAV_KEYS.LOS,
  edlos: NAV_KEYS.EDLOS,
  watchlist: NAV_KEYS.WATCHLIST,
  customerMemberList: NAV_KEYS.CUSTOMER_MEMBER_LIST,
  cml: NAV_KEYS.CUSTOMER_MEMBER_LIST,
  groupManagement: NAV_KEYS.GROUP_MANAGEMENT,
  groupManagementDashboard: NAV_KEYS.GROUP_MANAGEMENT_DASHBOARD,
};

/**
 * Resolves the correct navigation key for FGG family based on market configuration
 */
const getFrontlineNavKey = (
  selectedPeriod: string,
  configuration: Partial<Config> | undefined,
  isoCountryCode: string,
): NavigationKey => {
  if (!configuration) return NAV_KEYS.FRONTLINE_GROWTH;
  const viewId = getIncentiveViewID(AWARD_CODES.FGG, selectedPeriod, configuration as Config, isoCountryCode);
  if (viewId === "frontlineQ") return NAV_KEYS.FRONTLINE_Q;
  if (viewId === "leadershipPlus") return NAV_KEYS.LEADERSHIP_PLUS;
  return NAV_KEYS.FRONTLINE_GROWTH;
};

/**
 * Resolves the correct navigation key for Leadership Seminar based on market
 */
const getLeadershipSeminarNavKey = (aff?: string): NavigationKey => {
  if (!aff) return NAV_KEYS.LEADERSHIP_SEMINAR;

  // ESAN and ANZ markets use "Leadership Training Seminar"
  if (ESAN_MARKET_CODES.includes(aff) || ANZ_MARKET_CODES.includes(aff)) {
    return NAV_KEYS.LEADERSHIP_SEMINAR_ESAN;
  }

  return NAV_KEYS.LEADERSHIP_SEMINAR;
};

/**
 * Get the navigation key for any identity (incentive or report).
 * This handles all dynamic resolution logic (FGG family, Leadership Seminar, etc.)
 *
 * @param identity - The identity information (id, contentKey, awardCode)
 * @param options - Optional resolver options (period, market, config)
 * @returns The canonical navigation key for this identity
 */
export function getNavKeyForIdentity(identity: IdentityInfo, options: ResolverOptions = {}): NavigationKey {
  const { selectedPeriod = "", configuration, isoCountryCode = "", aff = "" } = options;

  // Try primary id first
  const primaryId = identity.id;
  const contentKey = identity.contentKey || primaryId;

  // Handle Leadership Seminar market-specific resolution
  if (primaryId === "leadershipSeminar" || contentKey === "leadershipSeminar") {
    return getLeadershipSeminarNavKey(aff);
  }

  // Handle FGG family dynamic resolution
  if (primaryId === "fgi" || contentKey === "fgi") {
    if (selectedPeriod && configuration && isoCountryCode) {
      return getFrontlineNavKey(selectedPeriod, configuration, isoCountryCode);
    }
    // Fallback to frontlineGrowth if options not provided.
    return NAV_KEYS.FRONTLINE_GROWTH;
  }

  // Try contentKey first (more specific), then id
  let navKey = IDENTITY_TO_NAV_KEY[contentKey] || IDENTITY_TO_NAV_KEY[primaryId];

  // Handle compound contentKeys like "bronzeIncentives.bfi" → extract suffix "bfi"
  if (!navKey && contentKey.includes(".")) {
    const suffix = contentKey.split(".").pop();
    if (suffix) {
      navKey = IDENTITY_TO_NAV_KEY[suffix];
    }
  }

  if (navKey) {
    return navKey;
  }

  console.warn(
    `[IncentiveRegistry] Unknown identity: id="${primaryId}", contentKey="${contentKey}", awardCode=${identity.awardCode}. ` +
      `Falling back to id="${primaryId}". Add this identity to IDENTITY_TO_NAV_KEY if it's a valid incentive/report.`,
  );

  // Fallback: use the id as-is (will likely fail translation, but won't break the app)
  return primaryId as NavigationKey;
}

/**
 * Get the fully translated, symbol-interpolated title for an identity.
 * This is the main function components should use to get canonical titles.
 *
 * @param identity - The identity information (id, contentKey, awardCode)
 * @param t - The i18next translation function
 * @param options - Optional resolver options (period, market, config, symbol)
 * @returns The fully translated and interpolated title string
 */
export function getCanonicalTitle(identity: IdentityInfo, t: TFunction, options: ResolverOptions = {}): string {
  const navKey = getNavKeyForIdentity(identity, options);
  const defaultValue = navigationContent.linkTitle[navKey];

  return String(
    t(`linkTitle.${navKey}`, defaultValue, {
      ns: "navigation",
      symbol: options.symbol ?? "+",
    }),
  );
}
